import { Download, FileUp, Upload } from "lucide-react";
import Papa from "papaparse";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";

export type ImportDataType = "company" | "department" | "personnel" | "material";

const TEMPLATES: Record<ImportDataType, { title: string; headers: string[] }> = {
  company: { title: "公司", headers: ["name", "code", "isActive"] },
  department: {
    title: "部门",
    headers: ["name", "companyCode", "parentCode", "isCostCenter", "managerEmail", "isActive"],
  },
  personnel: {
    title: "人员",
    headers: [
      "name",
      "employeeNo",
      "companyCode",
      "departmentCode",
      "managerEmployeeNo",
      "position",
      "phone",
      "email",
      "adid",
      "isActive",
    ],
  },
  material: {
    title: "物料",
    headers: [
      "name",
      "levelCode",
      "categoryName",
      "productName",
      "specName",
      "unit",
      "priceYuan",
      "lifeDays",
      "safetyStock",
      "companyCode",
    ],
  },
};

interface ImportError {
  row: number;
  message: string;
}

interface DryRunResult {
  valid: boolean;
  errors: ImportError[];
  preview: Record<string, unknown>[];
  total: number;
}

export function CsvImportDialog({
  open,
  onOpenChange,
  dataType,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataType: ImportDataType;
  onImported?: () => void;
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const template = TEMPLATES[dataType];
  const previewRows = useMemo(() => dryRun?.preview ?? [], [dryRun]);

  function reset() {
    setFileName("");
    setRows([]);
    setDryRun(null);
    setError(null);
  }

  function downloadTemplate() {
    const csv = Papa.unparse([Object.fromEntries(template.headers.map((header) => [header, ""]))]);
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${dataType}-template.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function parseFile(file: File) {
    setError(null);
    setDryRun(null);
    setFileName(file.name);
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsed = result.data.filter((row) =>
          Object.values(row).some((value) => String(value ?? "").trim() !== ""),
        );
        if (result.errors.length > 0) {
          setError(`CSV 解析失败：${result.errors[0]?.message ?? "未知错误"}`);
          setRows([]);
          return;
        }
        if (parsed.length > 500) {
          setError("单次最多 500 行，请拆分文件后导入");
          setRows([]);
          return;
        }
        setRows(parsed);
      },
      error: (parseError) => setError(`CSV 读取失败：${parseError.message}`),
    });
  }

  async function validate() {
    if (rows.length === 0) {
      toast.error("请先选择 CSV 文件");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<DryRunResult>("/api/v1/admin/imports", {
        method: "POST",
        body: JSON.stringify({
          dataType,
          mode: "create_only",
          dryRun: true,
          rows,
        }),
      });
      setDryRun(result);
      if (result.valid) toast.success(`校验通过，共 ${result.total} 行`);
      else toast.error(`有 ${result.errors.length} 行需要修正`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "校验失败");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!dryRun?.valid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<{ created: number }>("/api/v1/admin/imports", {
        method: "POST",
        body: JSON.stringify({
          dataType,
          mode: "create_only",
          dryRun: false,
          rows,
        }),
      });
      toast.success(`导入完成：新增 ${result.created} 条`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "导入失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>批量导入：{template.title}</DialogTitle>
          <DialogDescription>
            仅支持 UTF-8 CSV；最多 500 行。先校验、再确认导入，校验失败不会写入任何数据。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="h-4 w-4" />
              下载 CSV 模板
            </Button>
            <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-4 text-sm font-medium hover:bg-muted">
              <FileUp className="h-4 w-4" />
              选择 CSV 文件
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) parseFile(file);
                }}
              />
            </label>
            {fileName ? (
              <span className="text-xs text-muted-foreground">
                {fileName} · {rows.length} 行
              </span>
            ) : null}
          </div>

          {error ? (
            <Alert variant="danger" title="导入未完成">
              {error}
            </Alert>
          ) : null}

          {dryRun ? (
            <>
              <Alert
                variant={dryRun.valid ? "success" : "warning"}
                title={dryRun.valid ? "校验通过" : "校验未通过"}
              >
                {dryRun.valid
                  ? `共 ${dryRun.total} 行，可以确认导入。`
                  : `发现 ${dryRun.errors.length} 个问题，修正文件后重新校验。`}
              </Alert>
              {dryRun.errors.length > 0 ? (
                <div className="max-h-40 overflow-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>行号</TableHead>
                        <TableHead>原因</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dryRun.errors.map((item) => (
                        <TableRow key={`${item.row}-${item.message}`}>
                          <TableCell>{item.row}</TableCell>
                          <TableCell className="text-red-700">{item.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
              {previewRows.length > 0 ? (
                <div className="max-h-56 overflow-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(previewRows[0] ?? {}).map((key) => (
                          <TableHead key={key}>{key}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((row) => (
                        <TableRow key={JSON.stringify(row)}>
                          {Object.keys(previewRows[0] ?? {}).map((key) => (
                            <TableCell key={key}>{String(row[key] ?? "—")}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button variant="secondary" onClick={validate} disabled={busy || rows.length === 0}>
            <Upload className="h-4 w-4" />
            校验文件
          </Button>
          <Button onClick={commit} disabled={busy || !dryRun?.valid}>
            确认导入
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
